import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { CommonDialog, CommonDialogHeader } from '..';
import { CustomerRefundService } from '../../../api/services/customerRefundService';
import { updateRefundRequest } from '../../../store/Reducers/CustomerRefundSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { IProcessRefundRequest } from '../../../types/api/request/customerRefundRequest.d';
import { CustomerRefundStatus } from '../../../types/store';
import { IProcessRefundDialogProps, IProcessRefundDialogBodyProps } from '../../../types/dialog';

const statusColorMap: Record<CustomerRefundStatus, 'warning' | 'success' | 'error' | 'info'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  PROCESSING: 'info',
};

export const ProcessRefundDialog = ({
  selectedRefund,
  isOpen,
  setIsOpen,
  onProcessed,
}: IProcessRefundDialogProps) => {
  const dispatch = useDispatch();

  const onSubmit = async (payload: IProcessRefundRequest) => {
    if (!selectedRefund) return;
    try {
      const updated = await CustomerRefundService.processRefundRequest(selectedRefund.id, payload);
      dispatch(updateRefundRequest(updated));
      dispatch(
        showNotification({
          message: `Refund request #${selectedRefund.order_number} has been ${payload.status.toLowerCase()}.`,
        }),
      );
      setIsOpen(false);
      onProcessed();
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error?.response?.data?.message ?? 'Failed to process refund request.',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      header={
        <CommonDialogHeader title="Refund Request Details" onClose={() => setIsOpen(false)} />
      }
      body={
        selectedRefund ? (
          <DialogBody
            refund={selectedRefund}
            onSubmit={onSubmit}
            onClose={() => setIsOpen(false)}
          />
        ) : null
      }
      footer={undefined}
    />
  );
};

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" flexDirection="column" gap={0.3}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const DialogBody = ({ refund, onSubmit, onClose }: IProcessRefundDialogBodyProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IProcessRefundRequest>({
    defaultValues: { notes: refund.notes ?? '' },
  });

  const isProcessable = refund.status === 'PENDING' || refund.status === 'PROCESSING';

  const handleAction = (status: 'APPROVED' | 'REJECTED') =>
    handleSubmit((data) => onSubmit({ ...data, status }))();

  return (
    <Box display="flex" flexDirection="column" gap={2} mt={1}>
      {/* Status badge */}
      <Box display="flex" justifyContent="flex-end">
        <Chip
          label={refund.status}
          color={statusColorMap[refund.status]}
          size="small"
          sx={{ fontWeight: 700 }}
        />
      </Box>

      {/* Order & Customer info */}
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <DetailRow label="Order Number" value={refund.order_number} />
        <DetailRow
          label="Refund Amount"
          value={`₱${Number(refund.refund_amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`}
        />
        <DetailRow label="Customer" value={refund.customer_name} />
        <DetailRow label="Payment Method" value={refund.payment_method} />
        <DetailRow label="Email" value={refund.customer_email} />
        <DetailRow label="Phone" value={refund.customer_phone} />
      </Box>

      <Divider />

      {/* Reason */}
      <DetailRow label="Reason for Refund" value={refund.reason} />

      {/* Resolution details — shown for APPROVED and REJECTED */}
      {(refund.status === 'APPROVED' || refund.status === 'REJECTED') && (
        <>
          <Divider />
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <DetailRow label="Processed By" value={refund.processed_by ?? '—'} />
            <DetailRow
              label="Date Processed"
              value={
                refund.updated_at
                  ? new Date(refund.updated_at).toLocaleString('en-PH', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'
              }
            />
          </Box>
          {refund.status === 'REJECTED' && (
            <DetailRow label="Rejection Reason" value={refund.notes ?? '—'} />
          )}
          {refund.status === 'APPROVED' && refund.notes && (
            <DetailRow label="Admin Notes" value={refund.notes} />
          )}
        </>
      )}

      {/* Processing form — only for actionable statuses */}
      {isProcessable && (
        <>
          <Divider />
          <FormControl>
            <FormLabel>Rejection Reason / Admin Notes</FormLabel>
            <TextField
              placeholder="Required if rejecting. Optional for approvals."
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              error={!!errors.notes}
              helperText={errors.notes?.message}
              {...register('notes')}
            />
          </FormControl>

          <Box display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={isSubmitting}
              onClick={() => handleAction('REJECTED')}
            >
              {isSubmitting ? 'Processing...' : 'Reject'}
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={isSubmitting}
              onClick={() => handleAction('APPROVED')}
            >
              {isSubmitting ? 'Processing...' : 'Approve'}
            </Button>
          </Box>
        </>
      )}

      {!isProcessable && (
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Box>
      )}
    </Box>
  );
};
